/**
 * demoPhotos — the ONE module every demo gallery and every marketing
 * surface reads its photography from.  GENERATED FILE.
 *
 *   source of truth: scripts/media/gallery-manifest.public.json
 *                    (or .media-staging/gallery-manifest.json)
 *   regenerate with: node scripts/media/emit-demo-photos.mjs
 *
 * Do not hand-edit — re-run the script.  Its twin,
 * ios/Orbit/Orbit/Data/DemoPhotoLibrary.swift, is emitted from the
 * same pass with the same ids in the same order, which is how the
 * web and iOS demo casts stay honest about being the same studio.
 *
 * ── WHAT THESE ARE ───────────────────────────────────────────────
 * Rennan's own frames — two real client deliveries, encoded by
 * MEDIA-1 into four renditions each and uploaded to the PUBLIC
 * `delivery-photos` bucket under `demo/`.  They replace the Unsplash
 * placeholders the demo cast shipped with.
 *
 * ── PICK THE RIGHT RENDITION ─────────────────────────────────────
 *   grid      800px long edge, WebP  — JustifiedGallery tiles
 *   lightbox  2000px long edge, WebP — full-screen / large cards
 *   hero      2400px long edge, WebP — full-bleed covers + hero beds
 *   full      2400px long edge, JPEG — downloads + OG/social cards
 *
 * These files are ALREADY at their target size.  Do not pass them
 * through `src/lib/imageTransform.ts` — a second transform is pure
 * waste (see media.config.mjs's RENDITIONS header).
 *
 * Every entry carries TRUE oriented dimensions, so callers can set
 * width/height (or aspect-ratio) on the `<img>` and nothing reflows
 * as photos land.  `JustifiedGallery` in particular reads
 * `aspectRatio` up front and skips its load-then-reflow pass.
 *
 * ── URLS ARE BUILT, NOT BAKED ────────────────────────────────────
 * GAL-3 was a total gallery outage caused by a storage path that had
 * drifted from the bucket it was written to.  So the bucket, the
 * prefix and the extensions live in ONE place below and every URL is
 * derived — mirroring `storagePathFor()` / `publicUrlFor()` in
 * scripts/media/media.config.mjs.  If a re-upload moves the prefix,
 * that constant is the only thing that changes.
 */

import type { JustifiedItem } from '@/components/gallery/JustifiedGallery';

/** PUBLIC bucket — never point this at `deliverables`. */
const BUCKET = 'delivery-photos';

/** Namespace root inside the bucket for all demo/marketing assets. */
const PATH_PREFIX = 'demo';

/**
 * Project URL.  Read from the environment so a preview deploy
 * pointed at another project still resolves; the literal is the
 * committed fallback (same ref as supabase/config.toml) so the
 * marketing page can never render 118 broken images just because an
 * env var went missing on a static build.
 */
const SUPABASE_URL = (
  (import.meta.env?.VITE_SUPABASE_URL as string | undefined) ||
  "https://nnbjedmwowijzeruiknl.supabase.co"
).replace(/\/+$/, '');

export type Rendition = 'grid' | 'lightbox' | 'hero' | 'full';

const RENDITION_EXT: Record<Rendition, string> = {
  grid: 'webp',
  lightbox: 'webp',
  hero: 'webp',
  full: 'jpg',
};

/** Mirrors media.config.mjs `storagePathFor()` + `publicUrlFor()`. */
function buildUrl(
  gallerySlug: string,
  rendition: Rendition,
  photoSlug: string,
): string {
  const objectPath = `${PATH_PREFIX}/${gallerySlug}/${rendition}/${photoSlug}.${RENDITION_EXT[rendition]}`;
  return `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${objectPath}`;
}

export interface DemoPhoto {
  /** Stable manifest id — `<sourceGallerySlug>/<photoSlug>`. */
  id: string;
  /** Source gallery slug (the storage folder), NOT the demo gallery. */
  gallerySlug: string;
  slug: string;
  /** TRUE oriented pixel dimensions of the `hero` rendition. */
  width: number;
  height: number;
  /** width / height. Feed straight to JustifiedItem.aspectRatio. */
  aspectRatio: number;
  orientation: 'landscape' | 'portrait' | 'square';
  /** Meaningful alt text — these are portfolio pieces on a public page. */
  alt: string;
  urls: Record<Rendition, string>;
}

type Row = [slug: string, width: number, height: number, alt: string];

function make(gallerySlug: string, rows: Row[]): DemoPhoto[] {
  return rows.map(([slug, width, height, alt]) => ({
    id: `${gallerySlug}/${slug}`,
    gallerySlug,
    slug,
    width,
    height,
    aspectRatio: width / height,
    orientation:
      width > height ? 'landscape' : width < height ? 'portrait' : 'square',
    alt,
    urls: {
      grid: buildUrl(gallerySlug, 'grid', slug),
      lightbox: buildUrl(gallerySlug, 'lightbox', slug),
      hero: buildUrl(gallerySlug, 'hero', slug),
      full: buildUrl(gallerySlug, 'full', slug),
    },
  }));
}

// ──────────────────────────────────────────────────────────────────
// Demo gallery sets — keyed by the DEMO gallery id (demo-g-1 …), the
// same keys `demoOverlay.demoGalleryPhotos` and iOS
// `DemoSeed.galleryPhotos` use.  Frame 0 of each set is the cover.
// ──────────────────────────────────────────────────────────────────

/** Kayla + James Wedding — 24 frames from `highlights-part1`. */
const DEMO_G_1: DemoPhoto[] = make("highlights-part1", [
  ["bf8a0068", 2400, 1084, "Kayla + James Wedding — rainy pine-forest wedding, frame 1 of 24"],
  ["bf8a0002", 2400, 1332, "Kayla + James Wedding — rainy pine-forest wedding, frame 2 of 24"],
  ["bf8a0116", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 3 of 24"],
  ["bf8a0160", 1601, 2400, "The bride walking a wet forest trail under a clear umbrella"],
  ["bf8a0221", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 5 of 24"],
  ["bf8a0337", 2400, 1521, "Kayla + James Wedding — rainy pine-forest wedding, frame 6 of 24"],
  ["bf8a0363", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 7 of 24"],
  ["bf8a0545", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 8 of 24"],
  ["bf8a0661", 1920, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 9 of 24"],
  ["bf8a0864-copy1", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 10 of 24"],
  ["bf8a0886", 1601, 2400, "The couple embracing on an open road at dusk, the train spread out behind the bride"],
  ["bf8a0919", 2400, 1127, "Kayla + James Wedding — rainy pine-forest wedding, frame 12 of 24"],
  ["bf8a0936", 1920, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 13 of 24"],
  ["bf8a1064", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 14 of 24"],
  ["bf8a1200", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 15 of 24"],
  ["bf8a1329-copy1", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 16 of 24"],
  ["bf8a1622", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 17 of 24"],
  ["bf8a1672-copy1", 2400, 1601, "Kayla + James Wedding — rainy pine-forest wedding, frame 18 of 24"],
  ["bf8a9532", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 19 of 24"],
  ["bf8a9648", 2400, 1601, "Kayla + James Wedding — rainy pine-forest wedding, frame 20 of 24"],
  ["bf8a9752", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 21 of 24"],
  ["bf8a9865", 2400, 1601, "Kayla + James Wedding — rainy pine-forest wedding, frame 22 of 24"],
  ["bf8a9932", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 23 of 24"],
  ["bf8a9976", 1601, 2400, "Kayla + James Wedding — rainy pine-forest wedding, frame 24 of 24"],
]);

/** Nova Coffee — Brand Campaign — 18 frames from `gallery-one`. */
const DEMO_G_2: DemoPhoto[] = make("gallery-one", [
  ["bf8a8315", 2400, 1601, "Friends crossing a timber footbridge above a coastal inlet, one sitting on the boards"],
  ["1a6a2555", 1600, 2400, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 2 of 18"],
  ["bf8a5720", 2400, 1601, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 3 of 18"],
  ["bf8a5873", 2400, 1156, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 4 of 18"],
  ["bf8a5929", 2400, 1601, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 5 of 18"],
  ["bf8a5943", 2400, 1601, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 6 of 18"],
  ["bf8a6004", 1920, 2400, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 7 of 18"],
  ["bf8a6089", 2400, 1206, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 8 of 18"],
  ["bf8a6149", 2400, 1601, "A photographer filming a family on the sand beside a striped beach umbrella"],
  ["bf8a6188", 2400, 1601, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 10 of 18"],
  ["bf8a6246", 2400, 1601, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 11 of 18"],
  ["bf8a6296", 2400, 1601, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 12 of 18"],
  ["bf8a6471", 1601, 2400, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 13 of 18"],
  ["bf8a6987", 2400, 1601, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 14 of 18"],
  ["bf8a7071", 2400, 1327, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 15 of 18"],
  ["bf8a7707", 1601, 2400, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 16 of 18"],
  ["bf8a8426", 1601, 2400, "Nova Coffee — Brand Campaign — on-location brand session on the Northern California coast, frame 17 of 18"],
  ["bf8a8614", 2400, 1601, "Two people crossing a plank walkway over the water with fog behind them"],
]);

/** Marcus & Elle Engagement — 16 frames from `gallery-one`. */
const DEMO_G_3: DemoPhoto[] = make("gallery-one", [
  ["bf8a7993", 2400, 1601, "Figures on a weathered wooden dock as fog closes over the water behind them"],
  ["bf8a5704", 1920, 2400, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 2 of 16"],
  ["bf8a5874", 2400, 1424, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 3 of 16"],
  ["bf8a5930", 2400, 1601, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 4 of 16"],
  ["bf8a5970", 1920, 2400, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 5 of 16"],
  ["bf8a6079", 1601, 2400, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 6 of 16"],
  ["bf8a6159", 1601, 2400, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 7 of 16"],
  ["bf8a6327", 1601, 2400, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 8 of 16"],
  ["bf8a6430", 1601, 2400, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 9 of 16"],
  ["bf8a6941", 2400, 1601, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 10 of 16"],
  ["bf8a7362", 1601, 2400, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 11 of 16"],
  ["bf8a7625", 1601, 2400, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 12 of 16"],
  ["bf8a8165", 2400, 1601, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 13 of 16"],
  ["bf8a8395", 1601, 2400, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 14 of 16"],
  ["bf8a8588", 2400, 1601, "Marcus & Elle Engagement — golden-hour engagement session by the water, frame 15 of 16"],
  ["bf8a8636", 1601, 2400, "A couple walking hand in hand down the hillside toward the jetties"],
]);

/** Hollins Family — Spring Set — 22 frames from `highlights-part1`. */
const DEMO_G_4: DemoPhoto[] = make("highlights-part1", [
  ["bf8a0085", 2400, 1417, "A bride and groom kissing under a clear umbrella while guests shelter under their own, string lights overhead"],
  ["bf8a0004", 1920, 2400, "Hollins Family — Spring Set — family session among the pines, frame 2 of 22"],
  ["bf8a0133", 1350, 2400, "Hollins Family — Spring Set — family session among the pines, frame 3 of 22"],
  ["bf8a0200-copy1", 1601, 2400, "Hollins Family — Spring Set — family session among the pines, frame 4 of 22"],
  ["bf8a0243", 1601, 2400, "Hollins Family — Spring Set — family session among the pines, frame 5 of 22"],
  ["bf8a0343", 1601, 2400, "Hollins Family — Spring Set — family session among the pines, frame 6 of 22"],
  ["bf8a0517", 1600, 2400, "Hollins Family — Spring Set — family session among the pines, frame 7 of 22"],
  ["bf8a0659", 1600, 2400, "Hollins Family — Spring Set — family session among the pines, frame 8 of 22"],
  ["bf8a0815-copy1", 2400, 1601, "Hollins Family — Spring Set — family session among the pines, frame 9 of 22"],
  ["bf8a0879", 1633, 2090, "Hollins Family — Spring Set — family session among the pines, frame 10 of 22"],
  ["bf8a0918", 2400, 1601, "Hollins Family — Spring Set — family session among the pines, frame 11 of 22"],
  ["bf8a0944", 1601, 2400, "Hollins Family — Spring Set — family session among the pines, frame 12 of 22"],
  ["bf8a1076", 2400, 1601, "Hollins Family — Spring Set — family session among the pines, frame 13 of 22"],
  ["bf8a1220-copy1", 1920, 2400, "Hollins Family — Spring Set — family session among the pines, frame 14 of 22"],
  ["bf8a1464", 1586, 2400, "Hollins Family — Spring Set — family session among the pines, frame 15 of 22"],
  ["bf8a1624", 1601, 2400, "Hollins Family — Spring Set — family session among the pines, frame 16 of 22"],
  ["bf8a1693", 1601, 2400, "Hollins Family — Spring Set — family session among the pines, frame 17 of 22"],
  ["bf8a9595", 1601, 2400, "Hollins Family — Spring Set — family session among the pines, frame 18 of 22"],
  ["bf8a9725-copy1", 1920, 2400, "Hollins Family — Spring Set — family session among the pines, frame 19 of 22"],
  ["bf8a9828-copy1", 1920, 2400, "Hollins Family — Spring Set — family session among the pines, frame 20 of 22"],
  ["bf8a9903", 2400, 1465, "Hollins Family — Spring Set — family session among the pines, frame 21 of 22"],
  ["bf8a9969", 2400, 1601, "Hollins Family — Spring Set — family session among the pines, frame 22 of 22"],
]);

/** Dr. Reyes — Headshots — 12 frames from `gallery-one`. */
const DEMO_G_5: DemoPhoto[] = make("gallery-one", [
  ["bf8a5824", 2400, 1601, "Dr. Reyes — Headshots — portrait session on the coast, frame 1 of 12"],
  ["bf8a5705", 1601, 2400, "Dr. Reyes — Headshots — portrait session on the coast, frame 2 of 12"],
  ["bf8a5961-copy1", 1601, 2400, "Dr. Reyes — Headshots — portrait session on the coast, frame 3 of 12"],
  ["bf8a5977", 2400, 1601, "Dr. Reyes — Headshots — portrait session on the coast, frame 4 of 12"],
  ["bf8a6137", 1601, 2400, "Dr. Reyes — Headshots — portrait session on the coast, frame 5 of 12"],
  ["bf8a6403", 1601, 2400, "Dr. Reyes — Headshots — portrait session on the coast, frame 6 of 12"],
  ["bf8a6547", 1601, 2400, "Dr. Reyes — Headshots — portrait session on the coast, frame 7 of 12"],
  ["bf8a7371-copy1", 1601, 2400, "Dr. Reyes — Headshots — portrait session on the coast, frame 8 of 12"],
  ["bf8a7608", 2400, 1601, "Dr. Reyes — Headshots — portrait session on the coast, frame 9 of 12"],
  ["bf8a7969", 1601, 2400, "Dr. Reyes — Headshots — portrait session on the coast, frame 10 of 12"],
  ["bf8a8390", 1601, 2400, "Dr. Reyes — Headshots — portrait session on the coast, frame 11 of 12"],
  ["bf8a8624", 1601, 2400, "Dr. Reyes — Headshots — portrait session on the coast, frame 12 of 12"],
]);

/** Every demo gallery's frames, keyed by demo gallery id. */
export const demoGalleryPhotoSets: Record<string, DemoPhoto[]> = {
  "demo-g-1": DEMO_G_1,
  "demo-g-2": DEMO_G_2,
  "demo-g-3": DEMO_G_3,
  "demo-g-4": DEMO_G_4,
  "demo-g-5": DEMO_G_5,
};

/**
 * Cover frame per demo gallery — frame 0 of each set.  Landscape by
 * construction so a 100vh full-bleed hero has something to crop.
 */
export const demoGalleryCovers: Record<string, DemoPhoto> =
  Object.fromEntries(
    Object.entries(demoGalleryPhotoSets).map(([id, photos]) => [id, photos[0]]),
  );

/** Flat list of every frame used by the demo cast. */
export const allDemoPhotos: DemoPhoto[] = Object.values(
  demoGalleryPhotoSets,
).flat();

// ──────────────────────────────────────────────────────────────────
// Marketing subset — the 26 frames MEDIA-1 flagged
// `marketing: true`, balanced across both shoots and both
// orientations.  Every one carries hand-written alt text: they sit on
// a public page as a working photographer's portfolio.
// ──────────────────────────────────────────────────────────────────

const MARKETING_BY_SOURCE: Record<string, DemoPhoto[]> = {
  "gallery-one": make("gallery-one", [
    ["bf8a5916", 2400, 1601, "A lone figure on an empty beach below a fogged headland, turquoise surf running the length of the frame"],
    ["bf8a6085", 2400, 1601, "Two children running along the waterline on a wide, empty beach"],
    ["bf8a6149", 2400, 1601, "A photographer filming a family on the sand beside a striped beach umbrella"],
    ["bf8a7993", 2400, 1601, "Figures on a weathered wooden dock as fog closes over the water behind them"],
    ["bf8a8315", 2400, 1601, "Friends crossing a timber footbridge above a coastal inlet, one sitting on the boards"],
    ["bf8a8392", 1601, 2400, "Three friends holding onto each other on a bluff above the water, cameras slung at their hips"],
    ["bf8a8502", 2400, 1601, "A fogbank rolling in over a still lagoon and its wooden jetties"],
    ["bf8a8600", 2400, 1601, "A couple leaning on a weathered railing above a mirror-flat lagoon"],
    ["bf8a8614", 2400, 1601, "Two people crossing a plank walkway over the water with fog behind them"],
    ["bf8a8632", 1601, 2400, "Two photographers throwing their arms up on the hillside above the inlet"],
    ["bf8a8633", 1601, 2400, "A pair celebrating on the slope above the water, a camera still in one hand"],
    ["bf8a8636", 1601, 2400, "A couple walking hand in hand down the hillside toward the jetties"],
    ["bf8a8649", 1601, 2400, "The lagoon seen through a chain-link fence, a photographer at work on the far side"],
  ]),
  "highlights-part1": make("highlights-part1", [
    ["bf8a0085", 2400, 1417, "A bride and groom kissing under a clear umbrella while guests shelter under their own, string lights overhead"],
    ["bf8a0152", 1601, 2400, "A misty forest path between tall pines with a single figure far down the trail"],
    ["bf8a0160", 1601, 2400, "The bride walking a wet forest trail under a clear umbrella"],
    ["bf8a0168", 1601, 2400, "A couple in their finest walking arm in arm through the rain-soaked pines"],
    ["bf8a0200", 1601, 2400, "The groom holding an umbrella over the bride and her train on a rain-soaked path"],
    ["bf8a0352", 2400, 1601, "A long reception table set with white linen, greenery and white blooms"],
    ["bf8a0386", 2400, 1601, "A tall arrangement of white flowers on the bar with guests gathered behind it"],
    ["bf8a0583", 2400, 1601, "The wedding party together under a timber pavilion, leis over long dresses"],
    ["bf8a0795", 2400, 1078, "Guests around the long table at golden hour, the forest opening up behind them"],
    ["bf8a0886", 1601, 2400, "The couple embracing on an open road at dusk, the train spread out behind the bride"],
    ["bf8a0923", 1601, 2400, "The bride reaching out to the groom on the road at dusk, veil catching the wind"],
    ["bf8a1041", 2400, 1520, "Toasts around the long table, guests leaning in to listen"],
    ["bf8a9879", 2400, 1601, "The ceremony in a pine clearing, guests seated in a circle around the couple"],
  ]),
};

/** All 26 marketing-flagged frames. */
export const marketingPhotos: DemoPhoto[] =
  Object.values(MARKETING_BY_SOURCE).flat();

const byId = new Map(marketingPhotos.map((p) => [p.id, p]));

/**
 * Look up a marketing frame, or fall back to the first one.
 *
 * Deliberately total: these resolve at MODULE scope in marketing
 * components, so a throw here happens before React renders and no
 * error boundary — not even MKTG-3's SafeMount — can catch it.
 * Degrade, don't detonate.
 */
function pick(id: string): DemoPhoto {
  return byId.get(id) ?? marketingPhotos[0];
}

function picks(ids: string[]): DemoPhoto[] {
  return ids.map(pick);
}

/** Hero bed — the one image on the page that loads eagerly. */
export const marketingHeroPhoto: DemoPhoto = pick("highlights-part1/bf8a0085");

/** Full-bleed band under the hero. Alternating wide / tall. */
export const photoWallPhotos: DemoPhoto[] = picks([
  "highlights-part1/bf8a0795",
  "gallery-one/bf8a8632",
  "gallery-one/bf8a8315",
  "highlights-part1/bf8a0200",
  "highlights-part1/bf8a0352",
  "highlights-part1/bf8a0886",
  "gallery-one/bf8a5916",
  "gallery-one/bf8a8392",
  "highlights-part1/bf8a9879",
  "highlights-part1/bf8a0160",
  "gallery-one/bf8a8502",
  "highlights-part1/bf8a0923",
]);

/** Photographers tab — portfolio stills. */
export const photographerFrames: DemoPhoto[] = picks([
  "highlights-part1/bf8a0160",
  "highlights-part1/bf8a0386",
  "highlights-part1/bf8a0886",
]);

/** Videographers tab — the widest frames, so they read as film stills. */
export const videographerFrames: DemoPhoto[] = picks([
  "highlights-part1/bf8a0795",
  "highlights-part1/bf8a0085",
  "highlights-part1/bf8a1041",
]);

/** Spare portfolio frames for the feature run. */
export const featureFrames: DemoPhoto[] = picks([
  "gallery-one/bf8a6149",
  "highlights-part1/bf8a0583",
  "gallery-one/bf8a8600",
]);

// ──────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────

/**
 * Adapt to `JustifiedGallery`'s item shape.
 *
 * Defaults to the `grid` rendition (800px long edge, ~9–30 KB) —
 * the layout engine draws tiles at roughly 110–300px tall, so
 * anything larger is bandwidth thrown away, and the keynote demo
 * runs on hotel wifi.
 *
 * Passing `aspectRatio` up front is the point: without it the
 * component starts every tile at a 3:2 guess and reflows the row
 * once the image reports its natural size.
 */
export function toJustifiedItems(
  photos: DemoPhoto[],
  rendition: Rendition = 'grid',
): JustifiedItem[] {
  return photos.map((p) => ({
    id: p.id,
    src: p.urls[rendition],
    alt: p.alt,
    aspectRatio: p.aspectRatio,
  }));
}

/** Frames for one demo gallery, or `[]` if the id is unknown. */
export function demoPhotosFor(demoGalleryId: string): DemoPhoto[] {
  return demoGalleryPhotoSets[demoGalleryId] ?? [];
}
